import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL || '/api'}/information`;

const getToken = (getState) => {
  return getState().auth.user?.token ? `Bearer ${getState().auth.user.token}` : '';
};

export const getInformation = createAsyncThunk('information/getInformation', async (_, thunkAPI) => {
  try {
    const response = await axios.get(API_URL);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return thunkAPI.rejectWithValue(message);
  }
});

export const createInformation = createAsyncThunk('information/createInformation', async (infoData, thunkAPI) => {
  try {
    const config = {
      headers: {
        Authorization: getToken(thunkAPI.getState),
      },
    };
    const response = await axios.post(API_URL, infoData, config);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateInformation = createAsyncThunk('information/updateInformation', async ({ id, infoData }, thunkAPI) => {
  try {
    const config = {
      headers: {
        Authorization: getToken(thunkAPI.getState),
      },
    };
    const response = await axios.put(`${API_URL}/${id}`, infoData, config);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteInformation = createAsyncThunk('information/deleteInformation', async (id, thunkAPI) => {
  try {
    const config = {
      headers: {
        Authorization: getToken(thunkAPI.getState),
      },
    };
    await axios.delete(`${API_URL}/${id}`, config);
    return id;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  information: [],
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: '',
};

const informationSlice = createSlice({
  name: 'information',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.information = action.payload;
      })
      .addCase(getInformation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(createInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.information.push(action.payload);
      })
      .addCase(createInformation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const index = state.information.findIndex(info => info._id === action.payload._id);
        if (index !== -1) {
          state.information[index] = action.payload;
        }
      })
      .addCase(updateInformation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(deleteInformation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteInformation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.information = state.information.filter(info => info._id !== action.payload);
      })
      .addCase(deleteInformation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset } = informationSlice.actions;
export default informationSlice.reducer;
